import { useMemo } from 'react'
import { ObjectProperty } from '../../shared/types'
import { ChildObjects } from './child-objects'
import { DateRange, DateRangePicker } from './daterange-picker'
import { Input } from './input'
import { MultiSelect } from './multi-select'
import { Options } from './select'
import { Switch } from './switch'
import { Tooltip, TooltipWrap } from './tooltip'

type ObjectPropertyInputProps = Pick<
	ObjectProperty,
	'description' | 'name' | 'type' | 'options' | 'referenceTo'
> & {
	value?: unknown
	onChange: (value: string | number | string[] | null | boolean | undefined) => void
	inputProps?: Record<string, string>
	title?: string
	invert?: boolean
}

export function ObjectPropertyFilter({
	description,
	name,
	type,
	options,
	value = '',
	onChange,
	inputProps,
	...rest
}: ObjectPropertyInputProps) {
	const label = description ? (
		<Tooltip delay={25} content={description}>
			<TooltipWrap>
				<span className="underline decoration-dotted cursor-help">{name}</span>
			</TooltipWrap>
		</Tooltip>
	) : (
		name
	)

	const selectOptions = useMemo(() => {
		return options?.map(option => ({ value: option.id as string, label: option.name }))
	}, [options])

	switch (type) {
		case 'string':
			return (
				<Input label={label} maxLength={255} value={value as string} onChange={onChange} />
			)
		case 'number':
		case 'currency':
			return <Input label={label} type="number" value={value as string} onChange={onChange} />
		case 'single':
		case 'multi':
			return (
				<MultiSelect
					label={label}
					selectedKeys={(value || []) as string[]}
					options={selectOptions as Options}
					onSelectionChange={onChange as (selectedKeys: Array<string | number>) => void}
				/>
			)
		case 'reference':
			return (
				<ChildObjects
					name={name}
					label={label}
					selectedKeys={(value || []) as string[]}
					onSelectionChange={onChange as (selectedKeys: Array<string | number>) => void}
					referenceTo={rest.referenceTo as string}
				/>
			)
		case 'datetime':
			return (
				<DateRangePicker
					label={label}
					{...inputProps}
					value={value as DateRange}
					onChange={onChange as unknown as (range: DateRange) => void}
				/>
			)
		case 'boolean':
			return (
				<div>
					<div className="flex font-normal leading-tight text-xs mb-2">{rest.title}</div>
					<Switch
						label={label}
						{...inputProps}
						isSelected={rest.invert ? value === false : !!value}
						onChange={(isSelected: boolean) =>
							onChange(isSelected ? (rest.invert ? false : true) : undefined)
						}
					/>
				</div>
			)
		case 'text':
		case 'derived':
			// ignore for now
			return null
	}
}
